import React, { useState, useEffect } from 'react';
import { SixPillars } from './SixPillars';
import { JuridicalGnosis } from './JuridicalGnosis';
import { QumranInversion } from './QumranInversion';
import { DemonolatryRites } from './DemonolatryRites';
import { MagiciansTimeline } from '../components/history/MagiciansTimeline';

export type DoctrinalTab = 'all' | 'pillars' | 'juridical' | 'qumran' | 'rites' | 'lineage';

interface DoctrinalArchiveProps {
  initialTab?: DoctrinalTab;
}

export const DoctrinalArchive: React.FC<DoctrinalArchiveProps> = ({ initialTab = 'all' }) => {
  const [activeTab, setActiveTab] = useState<DoctrinalTab>(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const tabs = [
    { id: 'all' as DoctrinalTab, label: 'Full Archive', latin: 'Corpus Integrum' },
    { id: 'pillars' as DoctrinalTab, label: 'The Six Pillars', latin: 'Sex Columnae' },
    { id: 'juridical' as DoctrinalTab, label: 'Juridical Gnosis', latin: 'Das Buch Belial' },
    { id: 'qumran' as DoctrinalTab, label: 'Qumran Inversion', latin: 'Tres Metzudot' },
    { id: 'rites' as DoctrinalTab, label: 'Demonolatry & Rites', latin: 'Ritus et Enn' },
    { id: 'lineage' as DoctrinalTab, label: 'Historical Magicians', latin: 'Charta Magorum' },
  ];

  return (
    <div className="space-y-10" id="doctrinal-archive-container">
      {/* Doctrinal Section Header */}
      <div className="border-b border-iron-dark pb-6">
        <div className="inline-flex items-center gap-2 rounded border border-bronze-dark/80 bg-obsidian-deep/80 px-3 py-1 text-[11px] font-display tracking-widest text-gold-ancient uppercase mb-2">
          <span>ARCHIVUM ET DOGMA</span>
          <span>•</span>
          <span>CURRENT OF THE UNYOKED</span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-parchment-light uppercase tracking-tight">
          Doctrinal Treatises & Grimoiric Archive
        </h1>
        <p className="text-sm sm:text-base text-parchment-dim mt-2 max-w-3xl font-serif">
          The complete canonical scriptures, juridical forensic instruments, Dead Sea scroll inversions, 
          and operative liturgical rituals of King Belial.
        </p>

        {/* Sub-Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mt-6" role="tablist" aria-label="Doctrinal Archive Sub-tabs">
          {tabs.map((tab) => {
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isSelected}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded text-xs font-display tracking-wider uppercase transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright ${
                  isSelected
                    ? 'border border-gold-bright bg-obsidian-surface text-gold-bright shadow-glow-gold'
                    : 'border border-iron-dark bg-obsidian-deep text-parchment-dim hover:text-parchment-light hover:border-iron-base'
                }`}
              >
                <span>{tab.label}</span>
                <span className="hidden sm:inline text-[9px] font-mono opacity-70 ml-2">({tab.latin})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Render Subsections based on Active Tab */}
      <div className="space-y-16">
        {(activeTab === 'all' || activeTab === 'pillars') && (
          <section id="section-six-pillars" aria-label="The Six Pillars">
            <SixPillars />
          </section>
        )}

        {(activeTab === 'all' || activeTab === 'juridical') && (
          <section id="section-juridical" aria-label="Juridical Gnosis">
            <JuridicalGnosis />
          </section>
        )}

        {(activeTab === 'all' || activeTab === 'qumran') && (
          <section id="section-qumran" aria-label="Qumran Inversion">
            <QumranInversion />
          </section>
        )}

        {(activeTab === 'all' || activeTab === 'rites') && (
          <section id="section-rites" aria-label="Demonolatry Rites">
            <DemonolatryRites />
          </section>
        )}

        {(activeTab === 'all' || activeTab === 'lineage') && (
          <section id="section-lineage" aria-label="Historical Magicians Lineage">
            <MagiciansTimeline />
          </section>
        )}
      </div>
    </div>
  );
};

export default DoctrinalArchive;
